import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMeasure, { type RectReadOnly } from 'react-use-measure';
import cn from 'classnames';
import { cubicBezier, motion } from 'framer-motion';

import { convertRemToPixels } from '@/common/lib/convertRemtoPx';
import { toShortNumFormat } from '@/common/lib/toShortNumFormat';
import { type PlantTypeText } from '@/common/types/plant';
import { type ResourceTypeText } from '@/common/types/resource';
import {
  PlantComponent,
  ResourceComponent,
} from '@/components/helpers/ObjectsText';
import { Reward } from '@/components/ui/Reward/Reward';
import { selectChestRewards } from '@/redux/selectors/contractSelectors';
import { useAppSelector } from '@/redux/store';

import css from './styles.module.scss';

interface MotionRewardsProps {
  adsView: boolean;
  containerMeasure: RectReadOnly;
}

const MotionRewards: FC<MotionRewardsProps> = ({
  adsView,
  containerMeasure,
}) => {
  const [ref, measure] = useMeasure();
  const [initial, setInitial] = useState<
    null | { x: number; y: number; scale: number; opacity: number }[]
  >(null);
  const { t } = useTranslation();
  const rewardList = useAppSelector(selectChestRewards);
  const plantNames = PlantComponent(t);
  const resourceNames = ResourceComponent(t);
  const text: Record<string, string> = {
    cash: 'Cash',
    tgem: 'T-Gems',
    chip: t('Casino Chips'),
  };

  // const mockRewardList = [
  //   { kind: 'cash', value: 1000000 },
  //   { kind: 'tgem', value: 25 },
  //   { kind: 'wood', value: 10000 },
  // ];

  useEffect(() => {
    if (measure.width && containerMeasure.width) {
      const initialItemWidth = 76;
      const offsetLeft = 16;
      const finalItemWidth = (measure.width - convertRemToPixels(4)) / 3;
      const finalPositions = [
        { x: measure.left + convertRemToPixels(1.5), y: measure.bottom },
        {
          x: measure.left + convertRemToPixels(2) + finalItemWidth,
          y: measure.bottom,
        },
        {
          x: measure.right - convertRemToPixels(1.5) - finalItemWidth,
          y: measure.bottom,
        },
      ];

      const initialPosition = finalPositions.map(({ x, y }, index) => ({
        x: containerMeasure.left + offsetLeft + initialItemWidth * index - x,
        y: containerMeasure.bottom - y,
        scale: 76 / finalItemWidth,
        opacity: 0,
      }));

      setInitial(initialPosition);
    }
  }, [measure, containerMeasure]);

  return (
    <div
      ref={ref}
      className="flex gap-2 w-full px-6 h-[9.25rem] relative z-[2]"
    >
      {initial &&
        rewardList?.map(({ kind, value }, index) => {
          const label =
            kind in text
              ? text[kind]
              : kind in resourceNames
                ? resourceNames[kind as ResourceTypeText].nameMaterial
                : kind in plantNames
                  ? plantNames[kind as PlantTypeText].nameMaterial
                  : kind;

          return (
            <motion.div
              initial={initial[index]}
              animate={{ x: 0, y: 0, scale: 1, opacity: 1 }}
              transition={{
                ease: cubicBezier(0.68, -0.55, 0.27, 1.55),
                delay: 0.8 + index * 0.3,
                duration: 0.3,
              }}
              className="flex-1"
              key={index}
            >
              <Reward
                kind={kind}
                value={toShortNumFormat(
                  adsView ? Math.round(value * 1.2) : value,
                )}
                label={label || kind}
                imageSize={'4.5rem'}
                className={cn(css.reward, adsView && css.bonus)}
              />
            </motion.div>
          );
        })}
    </div>
  );
};

export { MotionRewards };
